import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  Chip,
  Link,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ChannelIcon from "../ChannelIcon";
import { i18n } from "../../translate/i18n";

/**
 * Ficha do plugin INSTALADO: o que ele faz, quem o mantém e para onde escrever
 * quando quebra.
 *
 * Por que existe: depois de instalado, o plugin some dentro do produto — vira
 * "uma aba que apareceu". Quando algo para de funcionar, quem administra a
 * empresa não tem como saber se cobra o time do produto, um parceiro ou o
 * próprio fornecedor do serviço por trás. Estes dados são declarados pelo
 * autor do plugin (`store` no metadata do SDK, contrato `IPluginStoreListing`)
 * e chegam aqui pelo GET /plugins.
 *
 * O diálogo mostra só o que foi declarado: campo ausente não vira "—" nem
 * texto inventado; a seção inteira some. Plugin que não declarou nada exibe um
 * aviso dizendo isso com todas as letras, porque "sem informação de suporte" é
 * em si um fato que o administrador precisa saber antes de depender dele.
 */
const useStyles = makeStyles((theme) => ({
  cabecalho: { display: "flex", alignItems: "center", gap: 12 },
  meta: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 },
  secao: { marginTop: 20 },
  versao: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 14,
    marginBottom: 2,
  },
  versaoNumero: { fontWeight: 700 },
  versaoData: { fontSize: 12 },
  rotulo: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: 6,
  },
  lista: { margin: 0, paddingLeft: 18 },
  contatos: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 },
  parceiro: {
    marginTop: 14,
    padding: 12,
    borderRadius: 8,
    border: `1px dashed ${theme.palette.divider}`,
  },
  aviso: {
    marginTop: 16,
    padding: 12,
    borderRadius: 8,
    background: theme.palette.action.hover,
  },
}));

/** Rótulo do tipo de mantenedor — traduzido por chave, não por texto fixo. */
const rotuloTipo = (kind) =>
  i18n.t(`plugins.sobre.mantenedorTipo.${kind}`, { defaultValue: kind });

export default function PluginAboutDialog({ open, plugin, onClose }) {
  const classes = useStyles();
  if (!plugin) return null;

  const store = plugin.store || null;
  const mantenedor = store?.maintainer || null;
  // O que a instância declara é o histórico ATÉ a versão que ela roda — ela não
  // sabe o que saiu depois. Serve para entender o que se ganhou ao atualizar.
  const changelog = Array.isArray(plugin.changelog) ? plugin.changelog : [];
  const parceiro = store?.partner || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className={classes.cabecalho}>
          <ChannelIcon channel={plugin.channel || plugin.id} style={{ fontSize: 34 }} />
          <div>
            <Typography variant="h6" style={{ lineHeight: 1.2 }}>
              {plugin.displayName || plugin.id}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {[store?.category, plugin.version && `v${plugin.version}`]
                .filter(Boolean)
                .join(" • ")}
            </Typography>
          </div>
        </div>
      </DialogTitle>

      <DialogContent dividers>
        {(store?.description || store?.summary || plugin.description) && (
          <Typography variant="body2" color="textSecondary">
            {store?.description || store?.summary || plugin.description}
          </Typography>
        )}

        <Divider style={{ marginTop: 20 }} />
        <div className={classes.secao}>
          <div className={classes.rotulo}>{i18n.t("plugins.sobre.instalacao")}</div>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("plugins.sobre.identificador", { id: plugin.id || plugin.channel })}
          </Typography>
          {plugin.baseUrl && (
            <Typography variant="body2" color="textSecondary">
              {i18n.t("plugins.sobre.endereco", { url: plugin.baseUrl })}
            </Typography>
          )}
          <Typography variant="body2" color="textSecondary">
            {i18n.t("plugins.sobre.execucao", {
              modo: plugin.isRemote
                ? i18n.t("plugins.sobre.execucaoRemota")
                : i18n.t("plugins.sobre.execucaoLocal"),
            })}
          </Typography>
        </div>

        {!!store?.highlights?.length && (
          <div className={classes.meta}>
            {store.highlights.map((h) => (
              <Chip key={h} size="small" label={h} />
            ))}
          </div>
        )}

        {mantenedor && (
          <div className={classes.secao}>
            <div className={classes.rotulo}>{i18n.t("plugins.sobre.quemMantem")}</div>
            <Typography variant="body2">
              <strong>{mantenedor.name}</strong>
              {mantenedor.kind && (
                <Chip
                  size="small"
                  label={rotuloTipo(mantenedor.kind)}
                  style={{ marginLeft: 8 }}
                />
              )}
            </Typography>
            {mantenedor.about && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                {mantenedor.about}
              </Typography>
            )}
          </div>
        )}

        {(mantenedor?.support || mantenedor?.email || mantenedor?.website) && (
          <div className={classes.secao}>
            <div className={classes.rotulo}>{i18n.t("plugins.sobre.suporte")}</div>
            {mantenedor.support && (
              <Typography variant="body2">{mantenedor.support}</Typography>
            )}
            <div className={classes.contatos}>
              {mantenedor.email && (
                <Button
                  size="small"
                  variant="outlined"
                  href={`mailto:${mantenedor.email}`}
                >
                  {mantenedor.email}
                </Button>
              )}
              {mantenedor.website && (
                <Button
                  size="small"
                  variant="outlined"
                  href={mantenedor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {i18n.t("plugins.sobre.site")}
                </Button>
              )}
            </div>
          </div>
        )}

        {parceiro && (
          <div className={classes.parceiro}>
            <div className={classes.rotulo}>{i18n.t("plugins.sobre.servicoTerceiro")}</div>
            <Typography variant="body2">
              <strong>{parceiro.name}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {parceiro.role}
            </Typography>
            {parceiro.website && (
              <Link
                href={parceiro.website}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
              >
                {parceiro.website}
              </Link>
            )}
          </div>
        )}

        {!!store?.sections?.length &&
          store.sections.map((s) => (
            <div className={classes.secao} key={s.title}>
              <div className={classes.rotulo}>{s.title}</div>
              <ul className={classes.lista}>
                {(s.items || []).map((item) => (
                  <li key={item}>
                    <Typography variant="body2">{item}</Typography>
                  </li>
                ))}
              </ul>
            </div>
          ))}

        {!!changelog.length && (
          <div className={classes.secao}>
            <div className={classes.rotulo}>{i18n.t("plugins.sobre.novidades")}</div>
            {changelog.map((release) => (
              <div key={release.version}>
                <div className={classes.versao}>
                  <Typography variant="body2" className={classes.versaoNumero}>
                    {`v${release.version}`}
                  </Typography>
                  {release.version === plugin.version && (
                    <Chip size="small" label={i18n.t("plugins.sobre.versaoAtual")} />
                  )}
                  {release.breaking && (
                    <Chip
                      size="small"
                      color="secondary"
                      label={i18n.t("plugins.sobre.mudancaImportante")}
                    />
                  )}
                  {release.date && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      className={classes.versaoData}
                    >
                      {release.date}
                    </Typography>
                  )}
                </div>
                <ul className={classes.lista}>
                  {release.notes.map((nota) => (
                    <li key={nota}>
                      <Typography variant="body2">{nota}</Typography>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {!store && (
          <div className={classes.aviso}>
            <Typography variant="body2">
              {i18n.t("plugins.sobre.semFicha")}
            </Typography>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          {i18n.t("plugins.sobre.fechar")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
